import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryKeys';
import { useAuth } from '../contexts/AuthContext';
import { useBusinessBranches, useOrders } from '../hooks/businessData';
import { useSse } from '../hooks/useSse';
import { LoadingScreen } from '../components/Ui';
import { ControlTowerBoard } from '../components/control-tower/ControlTowerBoard';
import { ControlTowerExceptions } from '../components/control-tower/ControlTowerExceptions';
import { ControlTowerFilters } from '../components/control-tower/ControlTowerFilters';
import { ControlTowerHeader } from '../components/control-tower/ControlTowerHeader';
import { ControlTowerMetrics } from '../components/control-tower/ControlTowerMetrics';
import { classifyOrder, resolveSelectedOrder } from '../components/control-tower/control-tower-utils';
import { ControlTowerOrderDrawer } from '../components/control-tower/ControlTowerOrderDrawer';
import type { DeliveryFilter, OrderView } from '../components/control-tower/control-tower-utils';

const ORDERS_STATUS = 'pendiente,preparacion,completado,cancelado,fantasma';

export default function ControlTowerPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [branchId, setBranchId] = useState(user?.branchId ?? '');
  const [filter, setFilter] = useState<DeliveryFilter>('todas');
  const [selectedOrder, setSelectedOrder] = useState<OrderView | null>(null);

  const canChooseBranch = user?.role === 'owner' || user?.role === 'manager';

  const { data: allBranches = [] } = useBusinessBranches(canChooseBranch);
  const branches = useMemo<{ id: string; name: string; active: boolean }[]>(() => {
    if (!canChooseBranch && user?.branchId) {
      return [{ id: user.branchId, name: 'Sucursal asignada', active: true }];
    }
    return allBranches.filter((branch) => branch.active);
  }, [canChooseBranch, user?.branchId, allBranches]);

  useEffect(() => {
    if (!branchId && branches.length) setBranchId(branches[0].id);
  }, [branchId, branches]);

  const { data: orders = [], isLoading } = useOrders(branchId, ORDERS_STATUS);
  const loading = isLoading && !branches.length;

  const invalidateRef = useRef(() => {});
  useEffect(() => {
    invalidateRef.current = () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
    };
  }, [queryClient]);
  const streamStatus = useSse(branchId, () => invalidateRef.current());

  const filtered = useMemo(
    () =>
      filter === 'todas'
        ? orders
        : orders.filter((order) => order.deliveryType === filter),
    [orders, filter],
  );

  useEffect(() => {
    setSelectedOrder((current) => resolveSelectedOrder(orders, current));
  }, [orders]);

  const byBucket = useMemo(() => {
    const ingreso: OrderView[] = [];
    const produccion: OrderView[] = [];
    const despacho: OrderView[] = [];
    const excepciones: OrderView[] = [];
    for (const order of filtered) {
      const bucket = classifyOrder(order);
      if (bucket === 'ingreso') ingreso.push(order);
      else if (bucket === 'produccion') produccion.push(order);
      else if (bucket === 'despacho') despacho.push(order);
      else excepciones.push(order);
    }
    return { ingreso, produccion, despacho, excepciones };
  }, [filtered]);

  if (loading) return <LoadingScreen label="Cargando torre de control…" />;

  return (
    <div className="grid gap-6">
      <ControlTowerHeader
        sseStatus={streamStatus}
        branches={branches}
        branchId={branchId}
        onBranchChange={setBranchId}
        canChooseBranch={canChooseBranch}
      />
      <ControlTowerFilters value={filter} onChange={setFilter} />
      <ControlTowerMetrics orders={filtered} />
      <ControlTowerBoard
        ingreso={byBucket.ingreso}
        produccion={byBucket.produccion}
        despacho={byBucket.despacho}
        onSelectOrder={setSelectedOrder}
      />
      <ControlTowerExceptions orders={byBucket.excepciones} />
      <ControlTowerOrderDrawer order={selectedOrder} open={Boolean(selectedOrder)} onOpenChange={(open) => { if (!open) setSelectedOrder(null); }} />
    </div>
  );
}
